/* eslint-disable @typescript-eslint/no-explicit-any */
import { effect, Injectable } from '@angular/core';
import { TranslateService } from '@ngx-translate/core';
import { EntityType } from 'breeze-client';
import { firstValueFrom } from 'rxjs';
import { first } from 'rxjs/operators';
import { IdentifierDtoInput, ShareItemType, ShareReferenceType, SourceTypes, UriKey, Uuid } from '../../../../../graphql';
import { BxDocumentSourceType } from '../../../../core/components/bx-documents/documents.model';
import { ShareRecipientsStore } from '../../../../core/components/bx-email-recipients/domain/share-recipients.store';
import { ShareConfigStore } from '../../../../core/components/digital-acceptance-side-form/domain/share-config.store';
import { BxLogger } from '../../../../core/services/bx-logger.service';
import { AsideModalSize, HelperService } from '../../../../core/services/helper.service';
import { LeadsService } from '../../../../core/services/leads.service';
import { Job, JobVariation, ShareConfig } from '../../../../data/models';
import { RepoClientsService } from '../../../../data/repositories/repo-clients.service';
import { RepoJobsService } from '../../../../data/repositories/repo-jobs.service';
import { RepoSettingsService } from '../../../../data/repositories/repo-settings.service';
import { BreezeService } from '../../../../data/services/breeze/breeze.service';
import { SharedDataService } from '../../../../data/services/shared-data.service';
import { ReportService } from '../../../../reports/report.service';
import { ScheduleCommonApiService } from '../../../../shared/schedule/domain/api/common/schedule-common.api.service';
import { NullErr } from '../../../../utils/helper-functions/null-error';
import { CustomLayoutsService } from '../../../estimates/custom-layouts/custom-layouts.service';
import { JobVariationDetailComponent } from './job-variation-detail.component';

@Injectable({ providedIn: 'root' })
export class JobVariationsService {
    translations = {
        variation: 'Variation',
        adjustment: 'Adjustment',
        shortVariation: 'Var',
    };

    #variation?: JobVariation;
    #pdfUrl?: string;
    #isAdjustment: boolean = false;
    #usingAdjustments: boolean;

    constructor(
        private translateService: TranslateService,
        private leadsService: LeadsService,
        private helperService: HelperService,
        private breezeService: BreezeService,
        private reportService: ReportService,
        private logger: BxLogger,
        private settings: RepoSettingsService,
        private repoJobs: RepoJobsService,
        private repoCustomers: RepoClientsService,
        private customLayoutsService: CustomLayoutsService,
        private sharedData: SharedDataService,
        private shareRecipientsStore: ShareRecipientsStore,
        private shareConfigStore: ShareConfigStore,
        private scheduleCommonApiService: ScheduleCommonApiService
    ) {
        this.translations.variation = this.translateService.instant('general.variation');
        this.translations.adjustment = this.translateService.instant('general.adjustment');
        this.translations.shortVariation = this.translateService.instant('general.var');
        this.#usingAdjustments =
            this.sharedData.UserData.tenant.BillingCountry === 'New Zealand' ||
            this.sharedData.UserData.tenant.BillingCountry === 'Australia';

        effect(() => {
            this.shareRecipientsStore.shareToClientPortal();
            this.createShareConfig();
        });
    }

    async viewVariationById({ jobId, variationId }: { variationId: Uuid; jobId: Uuid }) {
        const v = await this.loadVariationById(variationId, jobId);
        if (!v) return;
        return this._viewVariation(v);
    }

    async viewVariation(variation: JobVariation) {
        const v = await this.loadVariation(variation);
        return await this._viewVariation(v);
    }

    private async _viewVariation(variation: JobVariation) {
        const initialState = {
            variation,
        };
        const previousStatus = variation.Status;
        const modalRef = this.helperService.showModalAside(JobVariationDetailComponent, { initialState }, AsideModalSize.wide);

        const result = (await modalRef.content?.action.pipe(first()).toPromise()) ?? NullErr('viewVariation.modalRef.content was null');
        if (result.cancelled) {
            this.cancelVariation(result.variation);
            throw result;
        }
        await this.saveVariation(result.variation);
        await this.syncJobItemsWithVariation(result.variation);
        if (previousStatus === 'Accepted' && result.variation.Status !== 'Accepted') {
            await firstValueFrom(
                this.scheduleCommonApiService.deleteScheduleTasksForVariation({
                    referenceId: variation.ID_Job,
                    referenceType: 'JOB',
                    variationId: variation.ID_Variation,
                })
            );
        }
        if (result.variation.Status === 'Accepted') {
            var toAdd =
                previousStatus !== 'Accepted'
                    ? result.variation.JobVariationTasks?.filter((vt) => vt.ShowInSchedule && !vt.IsDeleted).map(
                          (vt) => vt.ID_JobVariationTask
                      )
                    : result.scheduleChanges?.added;
            await firstValueFrom(
                this.scheduleCommonApiService.updateScheduleTasksForVariation({
                    referenceId: variation.ID_Job,
                    referenceType: 'JOB',
                    variationId: variation.ID_Variation,
                    request: {
                        toAdd: [...(toAdd ?? [])],
                        toRemove: [...(result.scheduleChanges?.removed ?? [])],
                        suggestedParentName: `${this.translations.shortVariation} #${variation.VariationNo}: ${variation.Description}`,
                    },
                })
            );
        }
        await this.sendVariation(result.variation);
        return result.variation;
    }

    private async loadVariation(variation: JobVariation) {
        if (variation.entityAspect.entityState.isAdded()) {
            return variation;
        }
        return await this.loadVariationById(variation.ID_Variation, variation.ID_Job);
    }

    private async loadVariationById(variationId: Uuid, jobId: Uuid) {
        const data = await this.repoJobs.GetJobVariation(variationId, jobId);
        return data.results[0] as unknown as JobVariation;
    }

    private async sendVariation(variation: JobVariation) {
        if (variation.send) {
            // @ts-ignore TS2790
            delete variation.send;

            await this.shareVariation(variation);

            //Save from status change
            await this.repoJobs.Save();
            return variation;
        } else return variation;
    }

    createShareConfig() {
        if (!this.#variation || !this.#pdfUrl) return;

        const shareRequestIdentifiers: IdentifierDtoInput[] = [
            {
                key: UriKey.Job,
                value: this.#variation.ID_Job,
            },
            {
                key: UriKey.Variation,
                value: this.#variation.ID_Variation,
            },
        ];

        const shareConfig: ShareConfig = {
            type: UriKey.Variation,
            shareRequestIdentifiers: shareRequestIdentifiers,
            shareRequestItems: [],
            references: [{ Id: this.#variation.ID_Job, Type: BxDocumentSourceType.Job }],
            referenceId: this.#variation.ID_Job,
            referenceType: ShareReferenceType.Job,
        };

        shareConfig.shareRequestItems.push({
            type: ShareItemType.EmailVariation,
            identifiers: [],
            binaryUrl: this.#pdfUrl,
            description: '',
            category: '',
            sourceId: this.#variation.Job.ID_Job,
            sourceType: SourceTypes.Job,
        });

        if (this.#variation.Job.ID_Estimate) {
            shareConfig.references?.push({ Id: this.#variation.Job.ID_Estimate, Type: BxDocumentSourceType.Estimate });

            if (this.#variation.Job.Estimate.ID_Lead && this.shareRecipientsStore.shareToClientPortal()) {
                shareConfig.shareRequestItems.push({
                    type: this.#isAdjustment ? ShareItemType.ClientPortalAdjustment : ShareItemType.ClientPortalVariation,
                    identifiers: [{ key: UriKey.Lead, value: this.#variation.Job.Estimate.ID_Lead }],
                    binaryUrl: this.#pdfUrl,
                    description: '',
                    category: '',
                    sourceId: this.#variation.Job.ID_Job,
                    sourceType: SourceTypes.Job,
                });
            }
        }

        this.shareConfigStore.set(shareConfig);

        return shareConfig;
    }

    private async saveVariation(variation: JobVariation) {
        const entities = [];
        entities.push(variation, variation.Job);
        const variationTaskType = this.breezeService.manager.metadataStore.getEntityType('JobVariationTask') as EntityType;
        const planMeasurementType = this.breezeService.manager.metadataStore.getEntityType('PlanMeasurement') as EntityType;
        const changes = this.breezeService.manager.getChanges([variationTaskType, planMeasurementType]);
        Array.prototype.push.apply(entities, changes);
        await this.repoJobs.Save(entities, `${this.translations.variation} saved`);
        return variation;
    }

    private async syncJobItemsWithVariation(variation: JobVariation) {
        this.repoJobs.SyncJobItemsWithVariations(variation.Job);
        await this.repoJobs.Save();
        return variation;
    }

    private getPrintVariationParams(variation: JobVariation) {
        const variationType = variation.IsAdjustment && this.#usingAdjustments ? this.translations.adjustment : this.translations.variation;
        const documentType = variationType.replace(' ', '');
        const reportName = this.#usingAdjustments ? 'jobvariationv2' : 'jobvariation';
        return {
            reportName,
            reportTitle: `${variationType} Report`,
            params: {
                ID_Variation: variation.ID_Variation,
                CalculationMethod: variation.Job.CalculationMethod,
                ShowEstimateTotalIfCostPlus: this.settings.ShowEstimateTotalOnCostPlusVariation,
            },
            format: null,
            fileName: `${variation.Job.Number ? variation.Job.Number + '-' : ''}${documentType}-${variation.VariationNo}`,
        };
    }

    printVariation(variation: JobVariation) {
        const params = this.getPrintVariationParams(variation);
        // @ts-ignore TS2345
        this.reportService.generate(params);
    }

    async shareVariation(variation: JobVariation) {
        this.#variation = variation;
        const job = this.#variation.Job;
        const reportConfig = this.getPrintVariationParams(this.#variation);
        // @ts-ignore TS2322
        reportConfig.format = 'pdf';
        // try
        // @ts-ignore TS2345
        const result = await this.reportService.generate(reportConfig);
        // @ts-ignore TS2532
        this.#pdfUrl = result.data;

        if (this.#pdfUrl) {
            const n = this.#pdfUrl.lastIndexOf('/');
            const fileName = this.#pdfUrl.substring(n + 1, this.#pdfUrl.length);

            const doc = {
                sourceType: 'Variation',
                leadId: job.ID_Lead,
                storageFileName: job.ID_Job + '/' + fileName,
                storageUrl: this.#pdfUrl,
                isCategorised: true,
                fileName: fileName,
            };

            const fullJobDescription = job.Number + ' - ' + job.Description;
            this.#isAdjustment = this.#variation.IsAdjustment && this.#usingAdjustments;
            const variationType = this.#isAdjustment ? this.translations.adjustment : this.translations.variation;
            const body = `Hi <client-name>, \n \n${
                this.sharedData.UserData.tenant.CompanyName
            } has attached the following ${variationType.toLowerCase()} for you to view: ${fileName} \n \nYou can also view a PDF version of this ${variationType.toLowerCase()} in the ${
                this.translations.variation
            } section of your Client Portal as soon as it's been shared by your builder. \n \n`;
            const emailConfig = {
                Subject: `${fullJobDescription} - ${variationType}`,
                Body: body,
                StorageAttachments: [this.#pdfUrl],
                To: [],
                clientContacts: [],
                context: 'job',
                references: [
                    {
                        Type: 'job',
                        Id: job.ID_Job,
                    },
                ],
            };

            const shareConfig = this.createShareConfig();

            const documentSource = { Id: job.ID_Job, Type: 'Job' };

            if (job.ID_Lead) {
                const res = await this.leadsService.getClientInviteListByIdQuery(job.ID_Lead).toPromise();
                if (res) {
                    emailConfig.To = (res.data as any).individualsWithInviteStatus;
                    const to = await this.getEmailTo(job);
                    // @ts-ignore TS2322
                    emailConfig.clientContacts = to;
                    this.customLayoutsService.openDigitalAcceptanceShareForm(
                        emailConfig,
                        shareConfig, //TODO:BX-17532 Remove this as a param to DA form and just rely on store
                        null,
                        true,
                        5,
                        false,
                        job.ID_Lead,
                        documentSource,
                        false,
                        null,
                        () => {
                            this.handleShareSuccess(this.#variation);
                        },
                        undefined,
                        this.#variation.IsAdjustment && this.#usingAdjustments
                    );
                }
            } else {
                const to = await this.getEmailTo(job);
                // @ts-ignore TS2322
                emailConfig.clientContacts = to;
                this.customLayoutsService.openDigitalAcceptanceShareForm(
                    emailConfig,
                    shareConfig, //TODO:BX-17532 Remove this as a param to DA form and just rely on store
                    null,
                    true,
                    5,
                    false,
                    null,
                    documentSource,
                    false,
                    null,
                    () => {
                        this.handleShareSuccess(this.#variation);
                    },
                    undefined,
                    this.#variation.IsAdjustment && this.#usingAdjustments
                );
            }
        }
    }

    private cancelVariation(variation: JobVariation) {
        const variationTaskType = this.breezeService.manager.metadataStore.getEntityType('JobVariationTask') as EntityType;
        const planMeasurementType = this.breezeService.manager.metadataStore.getEntityType('PlanMeasurement') as EntityType;
        const changes = this.breezeService.manager.getChanges([variationTaskType, planMeasurementType]);
        changes.forEach((item) => {
            item.entityAspect.rejectChanges();
        });
        // must reject job first
        variation.Job.entityAspect.rejectChanges();
        variation.entityAspect.rejectChanges();
    }

    async newVariation(job: Job, initData?: any, items?: any[]) {
        const variation = this.repoJobs.CreateJobVariation(job, initData, this.translations.variation);
        if (items) {
            for (let i = 0; i < items.length; i++) {
                const item = items[i];
                const variationItem = this.repoJobs.CreateJobVariationTask(variation, item);
                // refresh totals
                variationItem.calculateTotal();
            }
        }

        return await this.viewVariation(variation);
    }

    private async getEmailTo(job: Job) {
        const defaultTo = [];
        if (job.ClientEmail) {
            defaultTo.push({
                email: job.ClientEmail,
                name: job.ClientName,
            });
        }
        if (job.ID_Customer) {
            const customer = await this.repoCustomers.Get(job.ID_Customer);
            const contacts = this.repoCustomers.GetEmailSMSContacts(customer);
            if (!contacts.email.length) {
                return defaultTo;
            } else {
                // setup isBusiness flag for a Business contact
                // which is used to decide whether to send only the first name in the email or the full name
                // for a business we use full name
                // @ts-ignore TS2532
                if (customer.IsBusiness) {
                    // @ts-ignore TS2339
                    const businessContact = contacts.email.find((c) => c.email === customer.Email);
                    if (businessContact) {
                        // @ts-ignore TS2339
                        businessContact.isBusiness = true;
                    }
                }
                return contacts.email;
            }
        } else {
            return defaultTo;
        }
    }

    // @ts-ignore TS7006
    private handleShareSuccess(variation) {
        if (variation.Status === 'Pending') {
            variation.Status = 'Sent';
        }
        this.logger.success(`${this.translations.variation} Shared`);
        this.repoJobs.Save();
    }
}
